import { useEffect } from "react";
import { BRAND } from "@blackthorn/shared";

export type SeoData = {
  title: string;
  description: string;
  /** Canonical path, e.g. "/docs/verification" */
  path?: string;
  image?: string;
  /** Optional keywords for this page */
  keywords?: string;
};

const SITE_URL = (import.meta as { env?: { VITE_SITE_URL?: string } }).env?.VITE_SITE_URL ?? "";

function setMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(path: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = new URL(path, SITE_URL || window.location.origin).href;
}

/**
 * Per-route SEO metadata. Renders nothing - it updates document.title and the
 * meta tags in <head> whenever the page (or docs slug) changes.
 */
export default function SEO({ title, description, path = "/", image, keywords }: SeoData) {
  useEffect(() => {
    const fullTitle = title.includes(BRAND.name) ? title : `${title} · ${BRAND.name}`;
    const url = new URL(path, SITE_URL || window.location.origin).href;
    const img = image ?? "https://cdn.discordapp.com/attachments/1559903092097421322/1559903233772621955/blackthorn-banner.gif?ex=6ad0c498&is=6acf7318&hm=8b3b0a66db70465503add31987f5c32b8dfcf5654489df810a664e585dc2efd1&";
    const imgAbs = new URL(img, SITE_URL || window.location.origin).href;

    document.title = fullTitle;

    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("name", "robots", "index, follow");

    // Open Graph
    setMeta("property", "og:site_name", BRAND.name);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", imgAbs);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", imgAbs);

    setCanonical(path);
  }, [title, description, path, image, keywords]);

  return null;
}
